import { DailyBonusModalProps } from '../types';
import { StyledDailyBonus } from '../style';
import { ActionButton } from '../../ActionButton';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import PopupSingleProduct from '../PopupSingleProduct';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { motion } from 'framer-motion';
import { Modal } from '@mui/material';
import { EFontWeights } from 'utils/enums';
import { createGradientOrSolid } from 'utils';
import '../style.scss';

const MODAL_EXIT_TIME = 300;
const PRODUCT_EXIT_TIME = 400;

const DailyBonusPopup = forwardRef(
  (
    {
      title,
      titleColor = 'white',
      titleWeight = EFontWeights.Bold,
      titleSize = '24px',
      backgroundImage,
      borderColor,
      borderWidth = 2,
      buttonText = 'Collect',
      products,
      onCollect,
      onClose,
      onAnimationSuccessEndNoPreview,
      modalContainer,
      isPreview = false,
      nextProducts,
      nextAvailableCollectUTC,
      showEvery,
      showCloseButton = true,
      isBadgeNextProduct = false,
    }: DailyBonusModalProps,
    ref,
  ) => {
    const [open, setOpen] = useState(true);
    const [showNextProduct, setShowNextProduct] = useState(false);
    const [timeLeft, setTimeLeft] = useState<any | null>(null);
    const [startExitAnimProduct, setStartExitAnimProduct] = useState(false);
    const [startExitAnimModal, setStartExitAnimModal] = useState(false);
    const [nextAvailableDate, setNextAvailableDate] = useState<
      Date | undefined
    >(undefined);

    useEffect(() => {
      if (isBadgeNextProduct) {
        setShowNextProduct(true);
      }
    }, [isBadgeNextProduct]);

    useEffect(() => {
      setNextAvailableDate(nextAvailableCollectUTC);
    }, [nextAvailableCollectUTC]);

    useEffect(() => {
      if (showNextProduct) {
        calculateTimeLeft();
      }
    }, [showNextProduct]);

    const calculateTimeLeft = () => {
      // for testing purposes
      let nextDate: Date = nextAvailableDate || new Date();
      if (showEvery && showEvery < 1440) {
        const currentDate = new Date();
        nextDate = new Date(currentDate.getTime() + showEvery * 60000);
      }

      if (nextDate) {
        const interval = setInterval(() => {
          const remainingTime = nextDate.getTime() - new Date().getTime();
          setTimeLeft(remainingTime);
          if (remainingTime < 1000) {
            clearInterval(interval);
            window.location.reload();
          }
        }, 1000);
        return () => clearInterval(interval);
      }
    };

    const formatTime = (milliseconds: number) => {
      if (!timeLeft) return '';
      if (timeLeft < 1000) {
        return '00:00:00';
      }
      const totalSeconds = Math.round(milliseconds / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const onCollectClicked = () => {
      if (isPreview) return;
      onCollect();
    };

    const onOrderSuccess = () => {
      if (!isBadgeNextProduct) {
        setStartExitAnimProduct(true);
        if (nextProducts && nextProducts.length > 0) {
          setTimeout(() => {
            setShowNextProduct(true);
          }, PRODUCT_EXIT_TIME);
        } else {
          setTimeout(() => {
            setStartExitAnimModal(true);
          }, PRODUCT_EXIT_TIME);
          setTimeout(() => {
            setOpen(false);
            if (onAnimationSuccessEndNoPreview)
              onAnimationSuccessEndNoPreview();
          }, PRODUCT_EXIT_TIME + MODAL_EXIT_TIME);
        }
      }
    };

    const onOrderFail = () => {
      // console.log('on order fail');
      // setStartExitAnimProduct(true);
      // onCloseClick();
    };

    const onCloseClick = () => {
      if (isPreview) return;
      setStartExitAnimModal(true);
      setTimeout(() => {
        setOpen(false);
        if (onClose && !isBadgeNextProduct)
          onClose({ isShowingNextProduct: showNextProduct });
      }, MODAL_EXIT_TIME);
    };

    const productVariantsAnimation = {
      regular: { y: 0 },
      exit: {
        y: [0, -200],
        opacity: [1, 0],
      },
    };

    const bannerVariantsAnimation = {
      enter: { scale: [0, 1.1, 1], transition: { duration: 0.4 } },
      exit: {
        scale: [1, 0],
        transition: { duration: 0.2 },
      },
    };

    useImperativeHandle(
      ref,
      (): { onOrderFail: () => void; onOrderSuccess: () => void } => ({
        onOrderSuccess,
        onOrderFail,
      }),
    );

    return (
      <Modal
        disableEnforceFocus={isPreview}
        className={`daily-bonus-modal ${
          isPreview ? 'daily-bonus-modal__preview' : ''
        }`}
        open={open}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        container={modalContainer}
      >
        <motion.div
          variants={bannerVariantsAnimation}
          animate={startExitAnimModal ? 'exit' : 'enter'}
          className={'daily-bonus-border'}
          style={{
            background:
                borderWidth === 0
                    ? 'transparent'
                    : borderColor
                        ? createGradientOrSolid(borderColor)
                        : 'var(--bundle-border-color)',
            padding: borderWidth + 'px',
          }}
        >
          <StyledDailyBonus
            titleColor={titleColor}
            titleWeight={titleWeight}
            titleSize={titleSize}
            backgroundImage={backgroundImage}
          >
            <div className="daily-bonus-banner">
              {(showCloseButton || showNextProduct) && (
                <button
                  className={'daily-bonus-banner__close-icon'}
                  onClick={() => onCloseClick()}
                >
                  <ClearOutlinedIcon
                    className="closeButton"
                    style={{ color: titleColor }}
                  />
                </button>
              )}
              <h2 className="daily-bonus-banner__title">{title}</h2>
              {showNextProduct && nextProducts && nextProducts.length > 0 ? (
                <>
                  <motion.div
                    animate={{ scale: [0, 1.1, 1] }}
                    transition={{
                      duration: 0.3,
                      delay: 0.2,
                    }}
                    className="daily-bonus-banner__product-container"
                  >
                    <PopupSingleProduct
                      quantityDisplay={nextProducts[0].quantityDisplay}
                      images={nextProducts[0]?.images}
                      textColor={nextProducts[0]?.textFontColorHex}
                    />
                  </motion.div>
                  <ActionButton
                    className="daily-bonus-banner__action-button daily-bonus-banner__action-button--disabled"
                    text={formatTime(timeLeft || 0)}
                    colors={{ colorOne: 'grey' }}
                    borderRadius={'7px'}
                    width={'100%'}
                    icon={'https://media.appcharge.com/defaults/lock-icon.png'}
                    isLoading={!timeLeft}
                  />
                </>
              ) : (
                !isBadgeNextProduct && (
                  <>
                    {/* todo: add support in multiple products when needed */}
                    {products && products?.length > 0 && (
                      <motion.div
                        variants={productVariantsAnimation}
                        animate={startExitAnimProduct ? 'exit' : 'regular'}
                        transition={{
                          duration: 0.3,
                          ease: 'easeInOut',
                        }}
                        className="daily-bonus-banner__product-container"
                      >
                        <PopupSingleProduct
                          quantityDisplay={products[0].quantityDisplay}
                          images={products[0]?.images}
                          textColor={products[0]?.textFontColorHex}
                        />
                      </motion.div>
                    )}
                    <motion.div
                      animate={{ scale: [0, 1, 1.05, 1] }}
                      transition={{
                        duration: 0.4,
                        delay: 0.2,
                      }}
                      style={{ width: '100%' }}
                    >
                      <motion.div whileHover={{ scale: 0.9 }}>
                        <ActionButton
                          className="daily-bonus-banner__action-button"
                          text={buttonText}
                          action={onCollectClicked}
                          borderRadius={'7px'}
                          width={'100%'}
                        />
                      </motion.div>
                    </motion.div>
                  </>
                )
              )}
            </div>
          </StyledDailyBonus>
        </motion.div>
      </Modal>
    );
  },
);

export default DailyBonusPopup;
